import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useTranslation} from "react-i18next"

const ENDPOINT = 'https://api.giphy.com/v1/gifs'
const SEARCH_DEBOUNCE_MS = 400

// Giphy's own response objects reach onSelect untouched — callers read
// images.downsized_medium off them.
const GifSearchBox = ({apiKey, onSelect, gifPerPage = 10, autoFocus = false}) => {
  const {t} = useTranslation()
  const [term, setTerm] = useState('')
  const [gifs, setGifs] = useState([])
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const inputRef = useRef(null)
  const requestId = useRef(0)

  const load = useCallback(async (query, from) => {
    const id = ++requestId.current
    setLoading(true)
    setFailed(false)
    const path = query ? `${ENDPOINT}/search?q=${encodeURIComponent(query)}&` : `${ENDPOINT}/trending?`
    try {
      const response = await fetch(`${path}api_key=${apiKey}&limit=${gifPerPage}&offset=${from}&rating=g`)
      if (!response.ok) throw new Error(response.status)
      const {data, pagination} = await response.json()
      if (id !== requestId.current) return
      setGifs(previous => from === 0 ? data : [...previous, ...data])
      setHasMore(from + data.length < pagination.total_count)
      setOffset(from + data.length)
    } catch (e) {
      if (id === requestId.current) setFailed(true)
    } finally {
      if (id === requestId.current) setLoading(false)
    }
  }, [apiKey, gifPerPage])

  useEffect(() => {
    const timer = setTimeout(() => load(term.trim(), 0), term ? SEARCH_DEBOUNCE_MS : 0)
    return () => clearTimeout(timer)
  }, [term, load])

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus()
  }, [autoFocus])

  return (
    <div className={'gif-box-wrapper'}>
      <input
        ref={inputRef}
        className={'gif-search'}
        type={'text'}
        value={term}
        onChange={event => setTerm(event.target.value)}
        placeholder={t('searchForGifs')}
        aria-label={t('searchForGifs')}
      />
      <div className={'gif-list-wrapper'}>
        <div className={'gif-grid'}>
          {gifs.map(gif => (
            <button key={gif.id} type={'button'} className={'gif'} onClick={() => onSelect(gif)}>
              <img src={gif.images.fixed_width.url} alt={gif.title || 'GIF'} loading={'lazy'}/>
            </button>
          ))}
        </div>
        {failed && <p className={'gif-message'}>{t('unexpectedError')}</p>}
        {!failed && !loading && gifs.length === 0 && <p className={'gif-message'}>{t('noGifsFound')}</p>}
        {hasMore && !loading && (
          <button type={'button'} className={'gif-more'} onClick={() => load(term.trim(), offset)}>
            {t('loadMore')}
          </button>
        )}
      </div>
    </div>
  )
}

export default GifSearchBox
